import { makeAutoObservable } from "mobx";

const uid = () =>
  (globalThis.crypto?.randomUUID?.() ?? `e_${Date.now()}_${Math.random().toString(16).slice(2)}`);

const DEFAULT_PERIODS = () => ([
  { id: "1H", label: "first_half", events: [] },
  { id: "2H", label: "second_half", events: [] },
]);

/**
 * Event shape:
 * {
 *   id: string,
 *   type: "goal" | "yellow" | "red" | "sub" | "own_goal" | "pen_scored" | "pen_missed",
 *   side: "side1" | "side2",
 *   time_min: number,          // minute (can be 0..130)
 *   player_id?: number|null,   // company_user_id
 *   player_name?: string|null
 *   assist_player_id?: number|null,
 *   note?: string|null
 * }
 */

export default class FutsalResultModel {
  type = "futsal_result_v1";
  periods = DEFAULT_PERIODS();
  extra_time = null; // { id:"ET", label:"Extra time", periods:[{id:"ET1"...},{id:"ET2"...}] } OR null
  penalties = null;  // { id:"PENS", label:"Penalties", events:[] } OR null

  constructor(details) {
    // details can be undefined/null
    if (details?.type === this.type) {
      this.periods = sanitizePeriods(details.periods) ?? DEFAULT_PERIODS();
      this.extra_time = sanitizeExtraTime(details.extra_time);
      this.penalties = sanitizePenalties(details.penalties);
    }
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // ------- computed (derived) -------

  get allPeriodsFlat() {
    const list = [];
    for (const p of this.periods) list.push({ scope: "REG", period: p });
    if (this.extra_time?.periods) {
      for (const p of this.extra_time.periods) list.push({ scope: "ET", period: p });
    }
    if (this.penalties?.events) {
      // penalties acts like a period with events
      list.push({ scope: "PENS", period: { id: "PENS", label: "penalties", events: this.penalties.events } });
    }
    return list;
  }

  get timeline() {
    // Flatten + sort by time, stable
    const out = [];
    for (const entry of this.allPeriodsFlat) {
      for (const e of entry.period.events ?? []) {
        out.push({ ...e, period_id: entry.period.id, period_label: entry.period.label, scope: entry.scope });
      }
    }
    return out.sort((a, b) => (a.time_min ?? 0) - (b.time_min ?? 0));
  }

  get goals() {
    return this.timeline.filter(e => e.type === "goal" || e.type === "own_goal");
  }

  get score() {
    let s1 = 0, s2 = 0;
    for (const g of this.goals) {
      // own_goal counts for opposite side
      if (g.type === "own_goal") {
        if (g.side === "side1") s2 += 1;
        else s1 += 1;
      } else {
        if (g.side === "side1") s1 += 1;
        else s2 += 1;
      }
    }
    return { side1: s1, side2: s2 };
  }

  get winner() {
    const { side1, side2 } = this.score;
    if (side1 === side2) return "draw";
    return side1 > side2 ? "side1" : "side2";
  }

  cardsBySide(side) {
    const yellow = this.timeline.filter(e => e.side === side && e.type === "yellow");
    const red = this.timeline.filter(e => e.side === side && e.type === "red");
    return { yellow, red };
  }

  // ------- mutations -------

  ensureExtraTime() {
    if (!this.extra_time) {
      this.extra_time = {
        id: "ET",
        label: "Extra time",
        periods: [
          { id: "ET1", label: "extended_time1", events: [] },
          { id: "ET2", label: "extended_time2", events: [] },
        ],
      };
    }
  }

  ensurePenalties() {
    if (!this.penalties) {
      this.penalties = { id: "PENS", label: "penalties", events: [] };
    }
  }

  addEvent({side="side1", periodId="1H", eventType="goal", timeMin=10, playerId, playerName='', note=''}) {
    const ev = {
      id: uid(),
      type: eventType,
      side,
      time_min: clampInt(timeMin, 0, 200),
      player_id: playerId,
      player_name: playerName,
      note,
    };
    this._pushEvent(periodId, ev);
    return ev.id;
  }

  removeEvent(eventId) {
    for (const entry of this.allPeriodsFlat) {
      const evs = entry.period.events;
      if (!Array.isArray(evs)) continue;
      const idx = evs.findIndex(e => e.id === eventId);
      if (idx >= 0) {
        evs.splice(idx, 1);
        return true;
      }
    }
    return false;
  }

  updateEvent(eventId, patch) {
    for (const entry of this.allPeriodsFlat) {
      const evs = entry.period.events;
      if (!Array.isArray(evs)) continue;
      const ev = evs.find(e => e.id === eventId);
      if (ev) {
        Object.assign(ev, patch);
        if (ev.time_min != null) ev.time_min = clampInt(ev.time_min, 0, 200);
        return true;
      }
    }
    return false;
  }

  _pushEvent(periodId, ev) {
    const p = this._findPeriod(periodId);
    if (!p) {
      // fallback to 1H
      this.periods[0].events.push(ev);
      return;
    }
    p.events.push(ev);
  }

  _findPeriod(periodId) {
    for (const p of this.periods) if (p.id === periodId) return p;
    if (this.extra_time?.periods) for (const p of this.extra_time.periods) if (p.id === periodId) return p;
    if (periodId === "PENS" && this.penalties) return { id: "PENS", label: "Penalties", events: this.penalties.events };
    return null;
  }

  // ------- serialization -------

  toJson() {
    return {
      type: this.type,
      periods: this.periods,
      extra_time: this.extra_time,
      penalties: this.penalties,
    };
  }

  /**
   * Mutates the "common" result fields based on football details.
   * Call this before saving.
   */
  recomputeCommon(resultObj) {
    if (!resultObj) return;
    resultObj.final = this.score;
    // allow draw
    resultObj.winner = this.winner === "draw" ? "draw" : this.winner;
  }
}

// --------- helpers ----------
function clampInt(v, min, max) {
  const n = Number(v);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.round(n)));
}

function sanitizePeriods(periods) {
  if (!Array.isArray(periods) || periods.length === 0) return null;
  return periods.map(p => ({
    id: String(p?.id ?? ""),
    label: String(p?.label ?? p?.id ?? ""),
    events: Array.isArray(p?.events) ? p.events : [],
  })).filter(p => p.id);
}

function sanitizeExtraTime(et) {
  if (!et) return null;
  const periods = sanitizePeriods(et.periods);
  if (!periods) return null;
  return { id: "ET", label: String(et.label ?? "Extra time"), periods };
}

function sanitizePenalties(p) {
  if (!p) return null;
  return {
    id: "PENS",
    label: String(p.label ?? "Penalties"),
    events: Array.isArray(p.events) ? p.events : [],
  };
}